/**
 * アプリ全体で共有するコンテキスト。
 *
 * Web (Vite + React) と React Native の双方から同じ Provider を使う。
 * 依存しているのは React と @tanstack/react-query だけで、
 * DOM API には触れていないため、そのままネイティブでも動作する。
 */
import { SpliClient } from "@spli/api-client";
import { createContext, createElement, useContext, useEffect, useMemo, useState } from "react";
import { disabledAdsConfig } from "./ads.js";
import { memoryTokenStorage } from "./session.js";
const SpliContext = createContext(null);
export function SpliProvider({ baseUrl, storage, ads, children }) {
    const tokenStorage = useMemo(() => storage ?? memoryTokenStorage(), [storage]);
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [initializing, setInitializing] = useState(true);
    // getToken はクライアント生成時に一度だけ渡すため、
    // 最新のトークンを参照できるよう箱 (ref 相当) を経由する
    const tokenRef = useMemo(() => ({ current: null }), []);
    tokenRef.current = token;
    const client = useMemo(() => new SpliClient({
        baseUrl,
        getToken: () => tokenRef.current,
        onUnauthorized: () => {
            tokenRef.current = null;
            setToken(null);
            setUser(null);
            void tokenStorage.remove();
        },
    }), [baseUrl, tokenRef, tokenStorage]);
    // 起動時に保存済みトークンからログイン状態を復元する
    useEffect(() => {
        let cancelled = false;
        const restore = async () => {
            const saved = await tokenStorage.get();
            if (!saved) {
                if (!cancelled)
                    setInitializing(false);
                return;
            }
            tokenRef.current = saved;
            if (!cancelled)
                setToken(saved);
            try {
                const me = await client.me();
                if (!cancelled)
                    setUser(me);
            }
            catch {
                // トークンが失効していた場合は未ログインとして続行する
                tokenRef.current = null;
                if (!cancelled)
                    setToken(null);
                await tokenStorage.remove();
            }
            finally {
                if (!cancelled)
                    setInitializing(false);
            }
        };
        void restore();
        return () => {
            cancelled = true;
        };
    }, [client, tokenRef, tokenStorage]);
    const value = useMemo(() => ({
        client,
        ads: ads ?? disabledAdsConfig,
        user,
        initializing,
        async setSession(newToken, newUser) {
            tokenRef.current = newToken;
            setToken(newToken);
            setUser(newUser);
            await tokenStorage.set(newToken);
        },
        async clearSession() {
            tokenRef.current = null;
            setToken(null);
            setUser(null);
            await tokenStorage.remove();
        },
    }), [ads, client, initializing, tokenRef, tokenStorage, user]);
    return createElement(SpliContext.Provider, { value }, children);
}
export function useSpli() {
    const value = useContext(SpliContext);
    if (!value)
        throw new Error("SpliProvider の中で使用してください");
    return value;
}
export function useSpliClient() {
    return useSpli().client;
}
export function useAdsConfig() {
    return useSpli().ads;
}
//# sourceMappingURL=context.js.map